import { useEffect, useRef, useState } from "react";
import { trackingService } from "../services/trackingService";
import { useGeoLocation } from "../hooks/useGeoLocation";
import { gtm } from "../utils/analytics";
import { initAllTracking, trackFormInteraction } from "../utils/trackingUtils";

const Tracker = ({
                     enableGeolocation = true,
                     enableGTM = true,
                     trackMouseMove = false,
                     throttleDelay = 1000
                 }) => {
    const sessionIdRef = useRef(crypto.randomUUID());
    const page = window.location.pathname;
    const userAgent = navigator.userAgent;
    const lastEventTimeRef = useRef({});
    const cleanupFunctionsRef = useRef([]);

    const [trackingEnabled, setTrackingEnabled] = useState(true);
    const { location, loading: locationLoading } = useGeoLocation();

    // Set global session ID
    useEffect(() => {
        window.platypusSessionId = sessionIdRef.current;
    }, []);

    // Throttle function to limit API calls
    const throttle = (eventType, callback, delay) => {
        const now = Date.now();
        const lastTime = lastEventTimeRef.current[eventType] || 0;

        if (now - lastTime >= delay) {
            lastEventTimeRef.current[eventType] = now;
            callback();
        }
    };

    // Enhanced interaction sending using both GTM and backend
    const sendInteraction = async (type, extraData = {}) => {
        if (!trackingEnabled) return;

        const payload = {
            type,
            sessionId: sessionIdRef.current,
            page,
            userAgent,
            timestamp: new Date().toISOString(),
            lat: location?.lat || null,
            lng: location?.lng || null,
            city: location?.city || null,
            country: location?.country || null,
            locationSource: location?.source || 'unknown',
            url: window.location.href,
            referrer: document.referrer,
            screenResolution: `${window.screen?.width || 0}x${window.screen?.height || 0}`,
            viewportSize: `${window.innerWidth}x${window.innerHeight}`,
            ...extraData,
        };

        try {
            // Send to your backend
            await trackingService.sendInteraction(payload);

            // Send to GTM if enabled
            if (enableGTM) {
                gtm.pushEvent(`custom_${type}`, {
                    event_category: 'user_interaction',
                    event_label: type,
                    ...extraData
                });
            }
        } catch (err) {
            // trackingService already handles logging, just disable on network errors
            if (err.toString().includes('Network') || err.toString().includes('fetch')) {
                console.warn("📶 Network issues detected, temporarily disabling tracking");
                setTrackingEnabled(false);

                // Re-enable after 5 minutes
                setTimeout(() => setTrackingEnabled(true), 5 * 60 * 1000);
            }
        }
    };

    // Initialize enhanced tracking when location is ready
    useEffect(() => {
        if (locationLoading || !trackingEnabled) return;

        // Track initial page view with location data
        sendInteraction("pageview", {
            title: document.title,
            loadTime: performance.now(),
            hasLocation: !!location?.lat,
            locationAccuracy: location?.source
        });

        // Send initial page view to GTM
        if (enableGTM) {
            gtm.trackPageView(page, document.title);
        }

        // Initialize all advanced tracking (scroll, sections, time)
        const cleanup = initAllTracking();
        cleanupFunctionsRef.current.push(cleanup);

        console.log("🎯 Enhanced tracking initialized with location:", location);

    }, [locationLoading, location, trackingEnabled, enableGTM, page]);

    // Register basic event listeners
    useEffect(() => {
        if (locationLoading || !trackingEnabled) return;

        // Throttled click handler
        const handleClick = (e) => {
            const target = e.target;
            throttle("click", () => {
                sendInteraction("click", {
                    x: e.clientX,
                    y: e.clientY,
                    element: target.tagName,
                    id: target.id,
                    className: target.className,
                    text: target.textContent?.slice(0, 100)
                });
            }, 500);
        };

        // Throttled scroll handler (basic - advanced scroll tracking is in trackingUtils)
        const handleScroll = () => {
            throttle("scroll", () => {
                const scrollPercent = Math.round(
                    (window.scrollY / (document.body.scrollHeight - window.innerHeight)) * 100
                );

                sendInteraction("scroll", {
                    scrollY: window.scrollY,
                    scrollPercent: isNaN(scrollPercent) ? 0 : scrollPercent
                });
            }, throttleDelay);
        };

        // Optional mouse move handler
        const handleMouseMove = (e) => {
            if (!trackMouseMove) return;

            throttle("mousemove", () => {
                sendInteraction("mousemove", {
                    x: e.clientX,
                    y: e.clientY
                });
            }, 2000);
        };

        // Enhanced form submit handler
        const handleFormSubmit = (e) => {
            const form = e.target;
            const formData = new FormData(form);
            const formFields = {};

            // Safely extract form field names (not values for privacy)
            for (let [key] of formData.entries()) {
                formFields[key] = 'submitted';
            }

            // Use the enhanced form tracking
            trackFormInteraction(form.id || 'unnamed-form', 'submit', {
                formAction: form.action,
                formMethod: form.method,
                fieldCount: Object.keys(formFields).length,
                fields: Object.keys(formFields)
            });
        };

        // Add event listeners
        window.addEventListener("click", handleClick);
        window.addEventListener("scroll", handleScroll);
        window.addEventListener("mousemove", handleMouseMove);
        document.addEventListener("submit", handleFormSubmit);

        // Track page unload
        const handleBeforeUnload = () => {
            sendInteraction("page_unload", {
                timeOnPage: performance.now(),
                scrollDepth: Math.round((window.scrollY / (document.body.scrollHeight - window.innerHeight)) * 100)
            });
        };

        window.addEventListener("beforeunload", handleBeforeUnload);

        // Cleanup
        return () => {
            window.removeEventListener("click", handleClick);
            window.removeEventListener("scroll", handleScroll);
            window.removeEventListener("mousemove", handleMouseMove);
            document.removeEventListener("submit", handleFormSubmit);
            window.removeEventListener("beforeunload", handleBeforeUnload);
        };
    }, [locationLoading, trackingEnabled, trackMouseMove, throttleDelay]);

    // Track route changes for SPA
    useEffect(() => {
        if (!trackingEnabled) return;

        sendInteraction("route_change", {
            newPage: window.location.pathname,
            previousPage: document.referrer
        });

        if (enableGTM) {
            gtm.trackPageView(window.location.pathname, document.title);
        }
    }, [window.location.pathname, trackingEnabled, enableGTM]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            cleanupFunctionsRef.current.forEach(cleanup => {
                if (typeof cleanup === 'function') {
                    cleanup();
                }
            });
        };
    }, []);

    return null;
};

export default Tracker;
